import React from "react";

/**
 * Reusable price display with optional struck-through MRP reference,
 * highlighted offer price, platform-discount badge and savings indicator.
 *
 * Props:
 *   pricing  : { original, offer, save_amount, save_pct, applied?, market_fee?, lawyer_fee_post_discount_rupees?, platform_discount_pct_bps? }
 *   layout   : "inline" | "stacked" | "card"
 *   size     : "sm" | "md" | "lg"
 *   showSave : boolean (default true)
 *   showFirstBadge : boolean (default true)
 */
export default function PriceTag({
  pricing,
  layout = "inline",
  size = "md",
  showSave = true,
  showFirstBadge = true,
  testid,
}) {
  if (!pricing) return null;
  const {
    original,
    offer,
    save_amount,
    save_pct,
    applied,
    market_fee,
    lawyer_fee_post_discount_rupees,
    platform_discount_pct_bps,
  } = pricing;

  const isFirst = applied === "first";

  // MRP reference: only use strikethrough/discount presentation when the MRP is
  // genuinely higher than the lawyer's post-discount fee. Never compare MRP with
  // the GST-inclusive customer payable.
  const postDiscountLawyerFee = lawyer_fee_post_discount_rupees ?? offer;
  const hasValidMrp = market_fee && market_fee > 0;
  const mrpIsHigher = hasValidMrp && market_fee > postDiscountLawyerFee;
  const referencePrice = mrpIsHigher ? market_fee : original;
  const computedSave = referencePrice && referencePrice > offer ? referencePrice - offer : 0;
  const computedSavePct = referencePrice && referencePrice > offer ? Math.round((computedSave / referencePrice) * 100) : 0;
  const displaySaveAmount = save_amount ?? computedSave;
  const displaySavePct = save_pct ?? computedSavePct;
  const hasDiscount = displaySaveAmount > 0 && offer < referencePrice;

  const platformDiscountPct = (platform_discount_pct_bps || 0) / 100;
  const showPlatformBadge = platformDiscountPct > 0 && !isFirst;

  const sizes = {
    sm: { offer: "text-base", original: "text-xs", save: "text-[10px]" },
    md: { offer: "text-2xl", original: "text-sm", save: "text-[11px]" },
    lg: { offer: "text-4xl", original: "text-base", save: "text-xs" },
  }[size];

  const PlatformDiscountBadge = () =>
    showPlatformBadge ? (
      <span
        className={`${sizes.save} font-bold inline-flex items-center gap-1 text-[#0A2540] bg-[#FBF4DF] border border-[#F1E3B5] rounded-full px-2 py-0.5`}
        data-testid={`${testid}-platform-discount`}
      >
        {platformDiscountPct.toFixed(0)}% OFF
      </span>
    ) : null;

  if (layout === "stacked") {
    return (
      <div data-testid={testid} className="flex flex-col">
        {mrpIsHigher && (
          <span className={`${sizes.original} text-slate-400 line-through font-medium`} data-testid={`${testid}-mrp`}>
            ₹{market_fee.toLocaleString("en-IN")}
          </span>
        )}
        <span
          className={`${sizes.offer} font-extrabold text-[#0A2540] leading-none`}
          style={{ fontFamily: "Poppins" }}
          data-testid={`${testid}-offer`}
        >
          ₹{offer.toLocaleString("en-IN")}
        </span>
        <span className="text-[10px] text-slate-400 mt-0.5" data-testid={`${testid}-gst-note`}>incl. GST</span>
        {isFirst && showFirstBadge && (
          <span
            className={`${sizes.save} mt-1 inline-flex items-center gap-1 font-bold text-[#0A2540] bg-[#FBF4DF] border border-[#F1E3B5] rounded-full px-2 py-0.5 self-start`}
            data-testid={`${testid}-first-badge`}
          >
            🎉 First Consultation Offer
          </span>
        )}
        <div className="flex items-center gap-1.5 flex-wrap mt-1">
          {showPlatformBadge && <PlatformDiscountBadge />}
          {showSave && hasDiscount && !isFirst && (
            <span
              className={`${sizes.save} inline-flex items-center gap-1 font-bold text-emerald-700`}
              data-testid={`${testid}-save`}
            >
              Save ₹{displaySaveAmount.toLocaleString("en-IN")} · {displaySavePct}% OFF
            </span>
          )}
        </div>
      </div>
    );
  }

  if (layout === "card") {
    return (
      <div data-testid={testid} className="flex flex-col gap-1">
        <div className="flex items-end gap-2 flex-wrap">
          <span
            className={`${sizes.offer} font-extrabold text-[#0A2540] leading-none`}
            style={{ fontFamily: "Poppins" }}
            data-testid={`${testid}-offer`}
          >
            ₹{offer.toLocaleString("en-IN")}
          </span>
          {mrpIsHigher && (
            <span className={`${sizes.original} text-slate-400 line-through font-medium leading-tight pb-0.5`} data-testid={`${testid}-mrp`}>
              ₹{market_fee.toLocaleString("en-IN")}
            </span>
          )}
          <div className="flex items-center gap-1">
            {showPlatformBadge && <PlatformDiscountBadge />}
            {showSave && hasDiscount && !isFirst && (
              <span
                className={`${sizes.save} font-bold text-white bg-emerald-600 rounded-md px-1.5 py-0.5 leading-tight`}
                data-testid={`${testid}-save-pct`}
              >
                {displaySavePct}% OFF
              </span>
            )}
          </div>
        </div>
        <span className="text-[10px] text-slate-400" data-testid={`${testid}-gst-note`}>incl. GST</span>
        {isFirst && showFirstBadge && (
          <span
            className="text-[10px] font-bold inline-flex items-center gap-1 text-[#0A2540] bg-[#FBF4DF] border border-[#F1E3B5] rounded-full px-2 py-0.5 self-start"
            data-testid={`${testid}-first-badge`}
          >
            🎉 First Consultation Offer
          </span>
        )}
      </div>
    );
  }

  // inline (default)
  return (
    <span data-testid={testid} className="inline-flex items-baseline gap-1.5 flex-wrap">
      {mrpIsHigher && (
        <span className={`${sizes.original} text-slate-400 line-through font-medium`} data-testid={`${testid}-mrp`}>
          ₹{market_fee.toLocaleString("en-IN")}
        </span>
      )}
      <span className={`${sizes.offer} font-extrabold text-[#0A2540]`} style={{ fontFamily: "Poppins" }} data-testid={`${testid}-offer`}>
        ₹{offer.toLocaleString("en-IN")}
      </span>
      <span className="text-[10px] text-slate-400" data-testid={`${testid}-gst-note`}>incl. GST</span>
      <div className="flex items-center gap-1">
        {showPlatformBadge && <PlatformDiscountBadge />}
        {isFirst && showFirstBadge ? (
          <span
            className={`${sizes.save} font-bold inline-flex items-center gap-1 text-[#0A2540] bg-[#FBF4DF] border border-[#F1E3B5] rounded-full px-2 py-0.5`}
            data-testid={`${testid}-first-badge`}
          >
            🎉 First Consultation Offer
          </span>
        ) : (
          showSave && hasDiscount && (
            <span className={`${sizes.save} font-bold text-emerald-700`} data-testid={`${testid}-save-pct`}>
              {displaySavePct}% OFF
            </span>
          )
        )}
      </div>
    </span>
  );
}

/** Convenience helper: pull the pricing for a given consultation type from a
 *  lawyer doc enriched by the backend.  Falls back to deriving from `fee` for
 *  any pre-discounted lawyer record (defensive). */
export function lawyerPricing(lawyer, type = "chat") {
  if (lawyer?.pricing?.[type]) return lawyer.pricing[type];
  // Defensive fallback (shouldn't usually happen since backend now always enriches)
  const mult = { chat: 1.0, audio: 1.2, video: 1.5 }[type] ?? 1.0;
  const original = Math.round((lawyer?.fee || 500) * mult);
  return { original, offer: original, save_amount: 0, save_pct: 0, applied: "none" };
}
